import { Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import classes from './funnel-shape.module.css';

/**
 * Why a band has no number.
 *
 * Two different absences that must not look alike: `too-young` is a cohort that
 * has not had time to fill, `not-built` is a rung nothing records yet. Reading
 * one as the other would turn "we have not measured this" into "we measured
 * nothing".
 */
export type BandAbsence = 'too-young' | 'not-built';

export interface FunnelBand {
  id: string;
  label: string;
  description: string;
  /** null whenever `absence` is set. */
  count: number | null;
  absence?: BandAbsence;
  /** Rendered beside the label — a sparkline, a badge. */
  aside?: ReactNode;
}

interface FunnelShapeProps {
  bands: FunnelBand[];
}

const format = new Intl.NumberFormat('en-GB');

function share(part: number, whole: number): string {
  if (whole === 0) return '—';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

/** Half-width of a band as a percentage of the full track, floored so a tiny rung stays visible. */
function halfWidth(count: number | null, base: number): number {
  if (count == null || base === 0) return 50;
  return Math.max(1.5, (count / base) * 50);
}

/**
 * The funnel as a tapering silhouette rather than a stack of bars.
 *
 * Left-aligned fills in full-width tracks read as a progress list — you have to
 * compare numbers to see that it is a funnel at all. Centring each band and
 * filling the shoulder between them makes the loss the negative space, which is
 * the shape the word "funnel" is promising.
 *
 * Built in CSS rather than SVG so the labels stay real text: they inherit the
 * theme, wrap properly, and are selectable.
 */
export function FunnelShape({ bands }: FunnelShapeProps) {
  const base = bands[0]?.count ?? 0;

  return (
    <div className={classes.funnel}>
      {bands.map((band, index) => {
        const next = bands[index + 1];
        const half = halfWidth(band.count, base);
        const nextHalf = next ? halfWidth(next.count, base) : half;
        const lost =
          band.count != null && next?.count != null
            ? band.count - next.count
            : null;

        return (
          <div key={band.id}>
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs">
              <Stack gap={0}>
                <Text size="sm" fw={600}>
                  {band.label}
                </Text>
                <Text size="xs" c="dimmed">
                  {band.description}
                </Text>
              </Stack>
              <Group gap="sm" wrap="nowrap" align="center">
                {band.aside}
                <Text className={classes.count}>
                  {band.count == null ? '—' : format.format(band.count)}
                </Text>
                {band.count != null && index > 0 && (
                  <Text size="xs" c="dimmed">
                    {share(band.count, base)}
                  </Text>
                )}
              </Group>
            </Group>

            {band.count == null ? (
              <div
                className={
                  band.absence === 'not-built'
                    ? classes.bandNotBuilt
                    : classes.bandEmpty
                }
              >
                <Text size="xs" c="dimmed">
                  {band.absence === 'not-built'
                    ? 'Not built — nothing records this yet'
                    : 'Not old enough to measure'}
                </Text>
              </div>
            ) : (
              <div className={classes.bandTrack}>
                <div
                  className={classes.band}
                  style={{ width: `${half * 2}%` }}
                />
              </div>
            )}

            {next && (
              <div className={classes.shoulderTrack}>
                {band.count != null && next.count != null && (
                  <div
                    className={classes.shoulder}
                    style={{
                      clipPath: `polygon(${50 - half}% 0%, ${50 + half}% 0%, ${50 + nextHalf}% 100%, ${50 - nextHalf}% 100%)`,
                    }}
                  />
                )}
                {lost != null && band.count != null && (
                  <Text size="xs" c="dimmed" className={classes.loss}>
                    −{format.format(lost)} ({share(lost, band.count)})
                  </Text>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
